import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Rocket } from "lucide-react";
import AssessmentDialog from "./AssessmentDialog";
import { showsLeadCta } from "@/lib/leadCtaRoutes";

/**
 * Floating "Request a Free Fleet Preservation Assessment" CTA for the Fleet
 * Preservation pages — same wording as the homepage hero's button. Centered
 * along the bottom edge on phones, bottom-right from md up (FloatingActions).
 *
 * Opens the Fleet Preservation Assessment form (see AssessmentDialog). It
 * takes the screen slot the Order Request cart uses elsewhere — see
 * lib/leadCtaRoutes.
 *
 * On the homepage the hero already leads with the same assessment button, so
 * this one stays hidden while the hero (#home-hero) is on screen and appears
 * once the visitor scrolls past it — no duplicate CTA, and it no longer sits
 * on top of the hero's slide dots on phones. It starts hidden there so the
 * prerendered HTML and the first client render agree.
 *
 * The trigger is a real <a> to /services/free-consultations/ so crawlers,
 * AI readers and no-JS visitors can follow it. With JS, the anchor's own
 * onClick prevents the navigation and opens the dialog through controlled
 * state. (Radix's DialogTrigger never calls preventDefault itself — verified
 * in @radix-ui/react-dialog source — so an <a> child would navigate away;
 * and its composed handler skips its own toggle once default is prevented,
 * which is why neither a bare preventDefault nor no handler at all works.
 * Driving `open` directly is the only correct pattern for a link trigger.)
 */
export default function GetStartedButton() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [heroInView, setHeroInView] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isHome) return;
    const hero = document.getElementById("home-hero");
    if (!hero || typeof IntersectionObserver === "undefined") {
      setHeroInView(false);
      return;
    }
    const io = new IntersectionObserver(([entry]) => setHeroInView(entry.isIntersecting));
    io.observe(hero);
    return () => io.disconnect();
  }, [isHome]);

  if (!showsLeadCta(pathname) || (isHome && heroInView)) return null;

  return (
    <AssessmentDialog open={open} onOpenChange={setOpen}>
      <a
        href="/services/free-consultations/"
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
        className="self-center inline-flex max-w-full items-center justify-center min-h-14 rounded-full shadow-lg px-5 py-2 text-center text-base leading-snug font-semibold bg-primary text-primary-foreground hover:bg-primary/90 md:self-end md:pr-6 md:text-lg"
      >
        <Rocket className="h-5 w-5 mr-2 shrink-0" />
        Request a Free Fleet Preservation Assessment
      </a>
    </AssessmentDialog>
  );
}
